import PluralizationRule from "./PluralizationRule";
import Interval from "./Interval";
import PluralizedParameter from "./PluralizedParameter";
import PluralizationParseError from "./PluralizationParseError";

export default class PluralizationParser {
    private readonly parameterRegex: RegExp = /\[({.*?)\]/g;
    private readonly parameterIdentifierRegex: RegExp = /{(.+?)}/;
    private readonly parameterIdentifierTransformPattern: string = '{$1-pluralized}';
    private readonly parameterRulesRegex: RegExp = /\[{.+}(.*?)\]/;
    private readonly ruleIntervalRegex: RegExp = /\(\d+[,]?\d*\)/;
    private readonly ruleIntervalPartsRegex: RegExp = /(\d+)[,]?(\d*)/;

    /*
     * Parses pluralization parameters from following syntax; example:
     *  [{0}(1)mrkev|(2,4)mrkve|mrkví]
     * Parts explanation:
     *  [...] Wraps the whole parameter.
     *  {...} This is parameter identification - used to identify value passed as localization parameter.
     *  (...) Integer interval made of one number or start and end number
     *        Interval is immediately followed by localized text for parameter of value in that interval.
     *        If the interval is missing, the value is considered default.
     *  |     Separates text variations for the particular interval (PluralizationRule).
     */
    public parsePluralizedParameters(sourceText: string): Array<PluralizedParameter> {
        let pluralizedParameters = new Array<PluralizedParameter>();

        const parametersSource = this.getParametersSource(sourceText);
        parametersSource.forEach(parameterSource => {
            const parameterIdentifier = this.getParameterIdentifier(parameterSource);
            const transformedIdentifier = this.transformParameterIdentifier(parameterIdentifier);
            const pluralizationRules = this.parsePluralizationRules(parameterSource);
            const parsedParameter = new PluralizedParameter(transformedIdentifier, pluralizationRules);
            pluralizedParameters.push(parsedParameter);
        });

        return pluralizedParameters;
    }

    public transformParameterIdentifier(originalIdentifier: string): string {
        return originalIdentifier.replace(this.parameterIdentifierRegex, this.parameterIdentifierTransformPattern);
    }

    public prepareSourceTextForPluralization(sourceText: string): string {
        var preparedText = sourceText;
        const parametersSource = this.getParametersSource(sourceText);
        parametersSource.forEach(parameterSource => {
            const parameterIdentifier = this.getParameterIdentifier(parameterSource);
            const transformedIdentifier = this.transformParameterIdentifier(parameterIdentifier);
            preparedText = preparedText.replace(parameterSource, transformedIdentifier);
        });
        return preparedText;
    }

    private getParametersSource(sourceText: string): string[] {
        return sourceText.match(this.parameterRegex) || [];
    }

    private getParameterIdentifier(parameterSource: string): string {
        const identifierMatch = parameterSource.match(this.parameterIdentifierRegex);
        if(identifierMatch == null) {
            throw new PluralizationParseError('Pluralization parameter does not contain parameter identifier!');
        }
        return identifierMatch[0];
    }

    private parsePluralizationRules(parameterSource: string): Array<PluralizationRule> {
        const rulesSource = this.getPluralizationRulesSource(parameterSource);
        let pluralizationRules = new Array<PluralizationRule>();
        rulesSource.forEach(ruleSource => {
            const parsedRule = this.parsePluralizationRule(ruleSource);
            pluralizationRules.push(parsedRule);
        });
        this.ensureOneDefaultRule(pluralizationRules);
        return this.ensureDefaultRuleAsTheLastOne(pluralizationRules);
    }

    private ensureOneDefaultRule(pluralizationRules: PluralizationRule[]): void {
        if(pluralizationRules.filter(rule => rule.isDefault).length > 1) {
            throw new PluralizationParseError('There are more than one default pluralization rules!');
        }
    }

    private ensureDefaultRuleAsTheLastOne(pluralizationRules: PluralizationRule[]): Array<PluralizationRule> {
        return pluralizationRules.sort(rule => rule.isDefault ? 1 : -1);
    }

    private getPluralizationRulesSource(parameterSource: string): string[] {
        const ruleSourceMatches = parameterSource.match(this.parameterRulesRegex);
        if(ruleSourceMatches == null || ruleSourceMatches[1] === '') {
            throw new PluralizationParseError('Could not find any pluralization rules!');
        }
        return ruleSourceMatches[1].split('|');
    }

    private parsePluralizationRule(pluralizationRuleSource: string): PluralizationRule {
        const intervalSource = this.getPluralizationRuleIntervalSource(pluralizationRuleSource);
        const interval = this.parseInterval(intervalSource);
        const ruleText = this.getPluralizationRuleText(pluralizationRuleSource);
        return new PluralizationRule(interval, ruleText);
    }

    private getPluralizationRuleIntervalSource(pluralizationRuleSource: string): string {
        const intervalMatches = pluralizationRuleSource.match(this.ruleIntervalRegex);
        return intervalMatches != null ? intervalMatches[0] : '';
    }

    private parseInterval(intervalSource: string): Interval {
        if(intervalSource === '') {
            return Interval.default;
        }
        const intervalMatch = intervalSource.match(this.ruleIntervalPartsRegex);
        return this.createIntervalFromMatch(intervalMatch);
    }

    private createIntervalFromMatch(intervalMatch: string[] | null): Interval {
        if(intervalMatch == null) {
            throw new PluralizationParseError('Interval is defined in wrong format!');
        }
        const start = Number(intervalMatch[1]);
        const end = intervalMatch[2] != '' ? Number(intervalMatch[2]) : start;
        return new Interval(start, end);
    }

    private getPluralizationRuleText(pluralizationRuleSource: string): string {
        return pluralizationRuleSource.replace(this.ruleIntervalRegex, '');
    }
}
